/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/settingsHelpers.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper functions for building settings arrays and extracting values.
────────────────────────────────────────────────────────────────*/

import type {
  ExtensionFormState,
  VoicemailSettingsState,
  RecordingSettingsState,
  ContactInfoState,
  FollowMeSettingsState,
} from '../types';
import { buildSoundPrefixPath } from './languageHelpers';

/**
 * Gets setting value from settings array
 */
export function getSettingValue(settings: Array<{ name: string; value?: string }> | undefined, name: string): string {
  const setting = settings?.find(s => s.name === name);
  return setting?.value || '';
}

/**
 * Builds settings array from form data
 */
export function buildSettings(
  formData: ExtensionFormState,
  recordingSettings: RecordingSettingsState,
  contactInfo: ContactInfoState,
  followMeData: FollowMeSettingsState
): Array<{ name: string; type: string; value: string; enabled: boolean }> {
  // Use soundPrefix from formData if available (full path), otherwise build it
  const soundPrefix = formData.soundPrefix || buildSoundPrefixPath(formData.languageCode, formData.voice);

  const settings = [
    { name: 'name', type: 'variable', value: formData.name, enabled: true },
    { name: 'features_password', type: 'variable', value: formData.featuresPassword, enabled: true },
    { name: 'vm-email-all-messages', type: 'variable', value: formData.emailAddress, enabled: true },
    { name: 'default_language', type: 'variable', value: formData.languageCode, enabled: true },
    { name: 'voice', type: 'variable', value: formData.voice, enabled: true },
    { name: 'sound_prefix', type: 'variable', value: soundPrefix, enabled: true },
    { name: 'effective_caller_id_name', type: 'variable', value: formData.internalCidName, enabled: true },
    { name: 'effective_caller_id_number', type: 'variable', value: formData.internalCidNumber, enabled: true },
    { name: 'outbound_caller_id_name', type: 'variable', value: formData.externalCidName, enabled: true },
    { name: 'outbound_caller_id_number', type: 'variable', value: formData.externalCidNumber, enabled: true },
    { name: 'emergency_caller_id_name', type: 'variable', value: formData.emergencyCidName, enabled: true },
    { name: 'emergency_caller_id_number', type: 'variable', value: formData.emergencyCidNumber, enabled: true },
    { name: 'accountcode', type: 'variable', value: formData.accountCode, enabled: true },
    { name: 'toll_allow', type: 'variable', value: formData.tollAllow, enabled: true },
    // Recording settings
    { name: 'record_incoming', type: 'variable', value: recordingSettings.recordIncoming.toString(), enabled: true },
    { name: 'record_outgoing', type: 'variable', value: recordingSettings.recordOutgoing.toString(), enabled: true },
    { name: 'record_internal', type: 'variable', value: recordingSettings.recordInternal.toString(), enabled: true },
    { name: 'record_on_demand', type: 'variable', value: recordingSettings.recordOnDemand.toString(), enabled: true },
    { name: 'recording_format', type: 'variable', value: recordingSettings.recordingFormat, enabled: true },
    { name: 'record_path', type: 'variable', value: recordingSettings.recordPath, enabled: true },
    { name: 'recording_filename_template', type: 'variable', value: recordingSettings.recordTemplate, enabled: true },
    // Contact info
    { name: 'contact_name_given', type: 'variable', value: contactInfo.name, enabled: true },
    { name: 'contact_email', type: 'variable', value: contactInfo.email, enabled: true },
    { name: 'contact_tel', type: 'variable', value: contactInfo.phone, enabled: true },
    { name: 'contact_note', type: 'variable', value: contactInfo.notes, enabled: true },
    // Advanced settings
    { name: 'call_timeout', type: 'variable', value: formData.callTimeout?.toString() || '30', enabled: true },
    { name: 'max_calls', type: 'variable', value: formData.maxCalls?.toString() || '0', enabled: true },
    { name: 'call_waiting', type: 'variable', value: formData.callWaiting?.toString() || 'true', enabled: true },
    { name: 'reject_anonymous', type: 'variable', value: formData.rejectAnonymous?.toString() || 'false', enabled: true },
    { name: 'auto_answer', type: 'variable', value: formData.autoAnswer?.toString() || 'false', enabled: true },
    { name: 'music_on_hold', type: 'variable', value: formData.holdMusic || '', enabled: !!formData.holdMusic },
    { name: 'privacy', type: 'variable', value: formData.privacy || 'none', enabled: true },
    { name: 'call_screen', type: 'variable', value: formData.callScreen?.toString() || 'false', enabled: true },
    { name: 'presence_id', type: 'variable', value: formData.presenceId || '', enabled: true },
    { name: 'ringtone', type: 'variable', value: formData.ringtone || '', enabled: true },
    { name: 'pinless', type: 'variable', value: formData.pinless?.toString() || 'false', enabled: true },
    // Call Forwarding settings
    { name: 'call_forward_enabled', type: 'variable', value: followMeData.callForwardEnabled.toString(), enabled: true },
    { name: 'call_forward_destination', type: 'variable', value: followMeData.callForwardDestination || '', enabled: followMeData.callForwardEnabled },
    { name: 'call_forward_time_group_id', type: 'variable', value: followMeData.callForwardTimeGroupId?.toString() || '', enabled: !!followMeData.callForwardTimeGroupId },
    { name: 'on_busy_enabled', type: 'variable', value: followMeData.onBusyEnabled.toString(), enabled: true },
    { name: 'on_busy_destination', type: 'variable', value: followMeData.onBusyDestination || '', enabled: followMeData.onBusyEnabled },
    { name: 'on_busy_time_group_id', type: 'variable', value: followMeData.onBusyTimeGroupId?.toString() || '', enabled: !!followMeData.onBusyTimeGroupId },
    { name: 'no_answer_enabled', type: 'variable', value: followMeData.noAnswerEnabled.toString(), enabled: true },
    { name: 'no_answer_destination', type: 'variable', value: followMeData.noAnswerDestination || '', enabled: followMeData.noAnswerEnabled },
    { name: 'no_answer_time_group_id', type: 'variable', value: followMeData.noAnswerTimeGroupId?.toString() || '', enabled: !!followMeData.noAnswerTimeGroupId },
    { name: 'not_registered_enabled', type: 'variable', value: followMeData.notRegisteredEnabled.toString(), enabled: true },
    { name: 'not_registered_destination', type: 'variable', value: followMeData.notRegisteredDestination || '', enabled: followMeData.notRegisteredEnabled },
    { name: 'not_registered_time_group_id', type: 'variable', value: followMeData.notRegisteredTimeGroupId?.toString() || '', enabled: !!followMeData.notRegisteredTimeGroupId },
    // Follow Me settings
    { name: 'follow_me_enabled', type: 'variable', value: followMeData.enabled.toString(), enabled: true },
    { name: 'follow_me_ignore_busy', type: 'variable', value: followMeData.ignoreBusy.toString(), enabled: followMeData.enabled },
    { name: 'do_not_disturb', type: 'variable', value: followMeData.doNotDisturb.toString(), enabled: true },
    { name: 'do_not_disturb_time_group_id', type: 'variable', value: followMeData.doNotDisturbTimeGroupId?.toString() || '', enabled: !!followMeData.doNotDisturbTimeGroupId },
  ];

  // Add Follow Me destinations
  if (followMeData.enabled && followMeData.destinations.length > 0) {
    // For backward compatibility, use the first destination as follow_me_number
    const firstDest = followMeData.destinations[0];
    settings.push({ name: 'follow_me_number', type: 'variable', value: firstDest.destination, enabled: true });
    settings.push({ name: 'follow_me_timeout', type: 'variable', value: firstDest.timeout.toString(), enabled: true });
    settings.push({ name: 'follow_me_delay', type: 'variable', value: firstDest.delay.toString(), enabled: true });
    if (firstDest.prompt) {
      settings.push({ name: 'follow_me_prompt', type: 'variable', value: firstDest.prompt, enabled: true });
    }

    // Store all destinations as JSON
    settings.push({
      name: 'follow_me_destinations',
      type: 'variable',
      value: JSON.stringify(followMeData.destinations.map(({ id, ...rest }) => rest)),
      enabled: true
    });
  }

  // NOTE: forward_to for virtual extensions is handled via the dedicated
  // virtual_forward_to column (sent as forwardTo in the payload), not in settings JSONB.

  // Add device settings for SIP extensions
  if (formData.extension_type === 'sip') {
    if (formData.deviceUsername) {
      settings.push({ name: 'user_id', type: 'variable', value: formData.deviceUsername, enabled: true });
    }
    if (formData.devicePassword) {
      settings.push({ name: 'password', type: 'variable', value: formData.devicePassword, enabled: true });
    }
    // NOTE: deviceDescription is now sent as device.description (direct column), not in JSONB settings
    if (formData.deviceMaxContacts) {
      settings.push({ name: 'max-registrations', type: 'variable', value: formData.deviceMaxContacts, enabled: true });
    }
    if (formData.deviceOutboundCodecs) {
      settings.push({ name: 'outbound-codec-prefs', type: 'variable', value: formData.deviceOutboundCodecs, enabled: true });
    }
    if (formData.deviceInboundCodecs) {
      settings.push({ name: 'inbound-codec-prefs', type: 'variable', value: formData.deviceInboundCodecs, enabled: true });
    }
    if (formData.deviceDtmfMode) {
      settings.push({ name: 'dtmf-type', type: 'variable', value: formData.deviceDtmfMode, enabled: true });
    }
  }

  return settings;
}

